/**
 * Kickard PHASE 3.1 — Guest Identity (persistent device-scoped name).
 *
 * Stored in localStorage as `kickard.guest.v1`:
 *   { guestId: "guest_xxxxxxxxxxxx",
 *     displayName: "Nenad",
 *     createdAt: 1700000000000,
 *     updatedAt: 1700000000000 }
 *
 * NOT an account. NOT authenticated. This is a local identity so the
 * player does not have to re-type their name every time. Server still
 * sees the chosen displayName as the lobby `name` field (no schema
 * change). Future login/profile can later upgrade or LINK this
 * guestId to a real account.
 *
 * Edge-cases handled:
 *   • localStorage unavailable → fall back to a module-level in-memory
 *     guest for the current session.
 *   • Invalid stored JSON → start fresh.
 *   • Empty / whitespace / too-long names → rejected by validate().
 *   • Old saved lobby session (kickard.online.session.v1) name is
 *     migrated automatically the first time a guest is read.
 */

export const GUEST_STORAGE_KEY = 'kickard.guest.v1';
const LEGACY_SESSION_KEY = 'kickard.online.session.v1';
// Phase 3.8.1 — first-party cookie used as a continuity bridge between
// the in-browser app and the installed PWA. iOS Safari historically did
// not share localStorage between the browser app and the
// "Add to Home Screen" PWA, so we ALSO write the guestId to a long-lived
// site cookie. On boot, if localStorage is missing the guest record but
// the cookie carries one, we restore the same profile (and fetch the
// server-canonical displayName by /api/guest-profile/by-id/{id}).
const GUEST_COOKIE = 'kickard_guest_id';
const GUEST_COOKIE_MAX_AGE_S = 60 * 60 * 24 * 365 * 2; // 2 years

function readGuestCookie(): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const cookies = (document.cookie || '').split(';');
    for (const raw of cookies) {
      const [k, ...rest] = raw.trim().split('=');
      if (k === GUEST_COOKIE && rest.length > 0) {
        return decodeURIComponent(rest.join('='));
      }
    }
  } catch { /* ignore */ }
  return null;
}

function writeGuestCookie(guestId: string): void {
  if (typeof document === 'undefined' || !guestId) return;
  try {
    // Same-Site Lax so the cookie persists from browser visit to PWA open
    // on the same origin/device. Secure so it only travels over https
    // (preview + production are both https).
    const expires = new Date(Date.now() + GUEST_COOKIE_MAX_AGE_S * 1000).toUTCString();
    document.cookie =
      `${GUEST_COOKIE}=${encodeURIComponent(guestId)}; ` +
      `Max-Age=${GUEST_COOKIE_MAX_AGE_S}; Expires=${expires}; Path=/; SameSite=Lax; Secure`;
  } catch { /* ignore */ }
}

export const NAME_MIN_LEN = 3;
export const NAME_MAX_LEN = 16;
// Unicode-aware: letters (incl. Serbian Latin č ć ž š đ Č Ć Ž Š Đ),
// digits, plus space . _ -. No emoji, no special chars.
const NAME_REGEX = /^[\p{L}\p{N} ._-]+$/u;

export interface Guest {
  guestId: string;
  displayName: string;
  createdAt: number;
  updatedAt: number;
}

// In-memory fallback when localStorage is unavailable (private mode,
// some embedded webviews, server-side render).
let memoryGuest: Guest | null = null;

function hasLocalStorage(): boolean {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    const probe = '__k_probe__';
    window.localStorage.setItem(probe, '1');
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

function readRaw(): Guest | null {
  if (!hasLocalStorage()) return memoryGuest;
  try {
    const raw = window.localStorage.getItem(GUEST_STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return null;
    if (typeof obj.guestId !== 'string' || !obj.guestId) return null;
    return {
      guestId: String(obj.guestId),
      displayName: typeof obj.displayName === 'string' ? obj.displayName : '',
      createdAt: typeof obj.createdAt === 'number' ? obj.createdAt : Date.now(),
      updatedAt: typeof obj.updatedAt === 'number' ? obj.updatedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

function writeRaw(guest: Guest): void {
  memoryGuest = guest; // mirror so loadGuest() returns same object even if LS fails
  // Phase 3.8.1 — also persist the guestId to a first-party cookie so the
  // installed PWA can recover the same profile on its first launch even if
  // localStorage is fresh (iOS in particular often has a separate storage
  // partition for standalone PWAs).
  if (guest.guestId) writeGuestCookie(guest.guestId);
  if (!hasLocalStorage()) return;
  try {
    window.localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(guest));
  } catch {
    /* quota / disabled — fall back to memory mirror */
  }
}

/** crypto.randomUUID where available, else Math.random fallback. */
function randomHex(length: number): string {
  try {
    const buf = new Uint8Array(length / 2);
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(buf);
      return Array.from(buf).map((b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    /* fall through */
  }
  let out = '';
  while (out.length < length) {
    out += Math.floor(Math.random() * 16).toString(16);
  }
  return out.slice(0, length);
}

export function generateGuestId(): string {
  return `guest_${randomHex(12)}`;
}

import { generateFunName } from '@/lib/guestNameGenerator';

// CRA bundler injects process.env at build time; declare it for TS.
declare const process: { env: { [key: string]: string | undefined } };

/** Suggested fallback: Kickard-flavoured fun name (e.g. "RedAce302"). */
export function suggestGuestName(): string {
  return generateFunName();
}

export interface NameValidation {
  ok: boolean;
  error?: string;
  cleaned: string;
}

export function validateName(raw: string): NameValidation {
  const cleaned = (raw || '').replace(/\s+/g, ' ').trim();
  if (!cleaned) return { ok: false, error: 'Name cannot be empty', cleaned };
  if (cleaned.length < NAME_MIN_LEN) {
    return { ok: false, error: `Name must be at least ${NAME_MIN_LEN} characters`, cleaned };
  }
  if (cleaned.length > NAME_MAX_LEN) {
    return { ok: false, error: `Name must be at most ${NAME_MAX_LEN} characters`, cleaned };
  }
  if (!NAME_REGEX.test(cleaned)) {
    return { ok: false, error: 'Use letters, numbers, spaces, . _ -', cleaned };
  }
  return { ok: true, cleaned };
}

/**
 * One-time legacy migration: if a saved online session (Phase 2) holds
 * a name and the guest record has no displayName yet, adopt that name
 * so returning players don't have to re-type.
 */
function migrateFromLegacySession(): string | null {
  if (!hasLocalStorage()) return null;
  try {
    const raw = window.localStorage.getItem(LEGACY_SESSION_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj.name !== 'string') return null;
    const v = validateName(obj.name);
    return v.ok ? v.cleaned : null;
  } catch {
    return null;
  }
}

/** Load the current guest, or null if no guest record exists yet. */
export function loadGuest(): Guest | null {
  return readRaw();
}

/**
 * Ensure a guest record exists. ALWAYS returns a Guest. If the record
 * is brand new, it will have an empty displayName (UI must prompt).
 * If a legacy lobby name exists, it is migrated.
 *
 * Phase 3.8.1 — if no localStorage guest record exists, FIRST try to
 * adopt the guestId from the `kickard_guest_id` first-party cookie so
 * that a player who installed the PWA after creating their identity in
 * the browser keeps the same guestId (and therefore the same H2H /
 * history / stats). `recoverDisplayNameFromServer()` must then be
 * called once on boot to fill in the canonical displayName from the
 * server profile.
 */
export function ensureGuest(): Guest {
  const existing = readRaw();
  if (existing) {
    // Backfill displayName from legacy lobby name if still missing.
    if (!existing.displayName) {
      const migrated = migrateFromLegacySession();
      if (migrated) {
        const updated = { ...existing, displayName: migrated, updatedAt: Date.now() };
        writeRaw(updated);
        return updated;
      }
    }
    // Refresh the cookie so it doesn't expire on returning users.
    writeGuestCookie(existing.guestId);
    return existing;
  }
  const migrated = migrateFromLegacySession();
  const now = Date.now();
  // PWA continuity: recover guestId from first-party cookie before minting
  // a brand-new one. displayName starts empty and gets filled async by
  // `recoverDisplayNameFromServer()` (lib/guestIdentity).
  const cookieGuestId = readGuestCookie();
  const guest: Guest = {
    guestId: cookieGuestId || generateGuestId(),
    displayName: migrated || '',
    createdAt: now,
    updatedAt: now,
  };
  writeRaw(guest);
  return guest;
}

/**
 * Phase 3.8.1 — async PWA continuity recovery.
 *
 * If the current guest record has a guestId but no displayName, hit
 * `GET /api/guest-profile/by-id/{guestId}` to fetch the canonical name
 * the user previously claimed (in the browser, before installing the
 * PWA). On success, fill `displayName` locally so the GuestNameModal
 * never prompts the same person for a new name on the same device.
 *
 * Returns true if a profile was successfully recovered.
 */
export async function recoverDisplayNameFromServer(): Promise<boolean> {
  const cur = readRaw();
  if (!cur || !cur.guestId) return false;
  if (cur.displayName) return false; // already have a name — nothing to do.
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  if (!BACKEND) return false;
  try {
    // PHASE 4.1.3 — include credentials so the durable server-issued
    // `kickard_guest_id` cookie travels with the request. This
    // enables device-scoped recovery on iOS Safari where client-set
    // cookies + localStorage may be wiped by ITP after 7 days of no
    // interaction, but the HTTP-Set-Cookie survives.
    const r = await fetch(
      `${BACKEND}/api/guest-profile/by-id/${encodeURIComponent(cur.guestId)}`,
      { credentials: 'include', cache: 'no-store' },
    );
    if (!r.ok) return false;
    const body = await r.json().catch(() => null);
    const name = body && typeof body.displayName === 'string' ? body.displayName : '';
    if (!name) return false;
    const updated: Guest = { ...cur, displayName: name, updatedAt: Date.now() };
    writeRaw(updated);
    return true;
  } catch {
    return false;
  }
}

/** Persist a new displayName for the current guest. */
export function setDisplayName(name: string): { ok: boolean; error?: string; guest?: Guest } {
  const v = validateName(name);
  if (!v.ok) return { ok: false, error: v.error };
  const cur = ensureGuest();
  const updated: Guest = { ...cur, displayName: v.cleaned, updatedAt: Date.now() };
  writeRaw(updated);
  return { ok: true, guest: updated };
}

/**
 * Phase 3.7.2 — server-authoritative display-name claim. Calls
 * `POST /api/guest-profile/claim`. On 200 success, also writes the
 * local guest record so the UI stays consistent. On 409 conflict,
 * returns `{ ok: false, taken: true, suggestions }`. The caller
 * (UI) is responsible for prompting the user with the suggestions.
 *
 * The guestId is NEVER changed by this call — that is the entire
 * point: changing your display name preserves your stats and H2H
 * because identity stays anchored to guestId.
 */
export async function claimDisplayName(name: string): Promise<{
  ok: boolean;
  guest?: Guest;
  error?: string;
  taken?: boolean;
  suggestions?: string[];
}> {
  const v = validateName(name);
  if (!v.ok) return { ok: false, error: v.error };
  const cur = ensureGuest();
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
  try {
    const r = await fetch(`${BACKEND}/api/guest-profile/claim`, {
      method: 'POST',
      credentials: 'include',  // PHASE 4.1.3 — receive the durable Set-Cookie
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ guestId: cur.guestId, displayName: v.cleaned }),
    });
    if (r.status === 409) {
      const body = await r.json().catch(() => ({}));
      return {
        ok: false,
        taken: true,
        error: 'Name already taken. Pick another.',
        suggestions: Array.isArray(body.suggestions) ? body.suggestions : [],
      };
    }
    if (!r.ok) {
      return { ok: false, error: 'Could not save name. Try again.' };
    }
    // Server accepted — mirror locally.
    const updated: Guest = { ...cur, displayName: v.cleaned, updatedAt: Date.now() };
    writeRaw(updated);
    return { ok: true, guest: updated };
  } catch {
    // Network truly unreachable — do NOT silently write the local name,
    // because that allows duplicate displayNames to land in match
    // history when the user comes back online. Surface the error.
    return { ok: false, error: 'Offline. Connect to claim your name.' };
  }
}

/** True iff a usable displayName is stored. */
export function hasDisplayName(): boolean {
  const g = loadGuest();
  return !!(g && g.displayName);
}

/** Convenience for callers that need ONLY the name. */
export function getDisplayName(): string {
  const g = loadGuest();
  return (g && g.displayName) || '';
}
